import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { ChartExportMenu } from '@/features/chart-export';
import type { ExportOptions, ChartExportOptions } from '@/shared/lib/export';
import type { ChartViewport } from '../lib/chart-options';
import { useChartViewport } from '../lib/use-chart-viewport';

interface EChartsWrapperProps {
  /**
   * Either a ready option, or a builder called with the room this chart actually
   * has. Charts whose axis labels have to fit take the builder — the window is a
   * poor proxy for a card that shares its row with another, and a zoomed-in
   * dataZoom window changes the room per category just as much as a resize does.
   */
  option: EChartsOption | ((viewport: ChartViewport) => EChartsOption);
  /**
   * Heading printed above the canvas, with the export menu beside it — an empty
   * one still keeps the row, so it does not jump in once the data arrives. The
   * chart keeps its own title hidden for the exported image.
   */
  title?: React.ReactNode;
  height?: number | string;
  className?: string;
  loading?: boolean;
  showExport?: boolean;
  exportOptions?: {
    png?: ChartExportOptions;
    csv?: ExportOptions;
    xlsx?: ExportOptions;
  };
  exportPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

const EXPORT_POSITION_CLASSES = {
  'top-left': 'absolute top-2 left-2 z-10',
  'top-right': 'absolute top-2 right-2 z-10',
  'bottom-left': 'absolute bottom-2 left-2 z-10',
  'bottom-right': 'absolute bottom-2 right-2 z-10'
} as const;

export const EChartsWrapper: React.FC<EChartsWrapperProps> = ({
  option,
  title,
  height = 400,
  className = '',
  loading = false,
  showExport = false,
  exportOptions,
  exportPosition = 'top-right'
}) => {
  const { t } = useTranslation();
  // ECharts prints its own English `loading` unless the option carries a text.
  const loadingOption = useMemo(() => ({ text: t('common.loading') }), [t]);

  const { containerRef, chartRef, resolvedOption, replaceMerge, chartEvents, isMeasured } = useChartViewport(option);

  const hasHeader = title !== undefined;
  const exportMenu = showExport && (
    <ChartExportMenu
      chartRef={chartRef}
      chartOption={resolvedOption}
      exportOptions={exportOptions}
      disabled={loading}
    />
  );

  const canvas = (
    <div ref={containerRef} className={`relative w-full ${hasHeader ? '' : className}`} style={{ height }}>
      {!hasHeader && exportMenu && <div className={EXPORT_POSITION_CLASSES[exportPosition]}>{exportMenu}</div>}
      {isMeasured && (
        <ReactECharts
          ref={chartRef}
          option={resolvedOption}
          replaceMerge={replaceMerge}
          style={{ height: '100%' }}
          showLoading={loading}
          loadingOption={loadingOption}
          theme="light"
          opts={{ renderer: 'canvas' }}
          onEvents={chartEvents}
        />
      )}
    </div>
  );

  if (!hasHeader) {
    return canvas;
  }

  return (
    <div className={`w-full ${className}`}>
      <div className="mb-2 grid min-h-8 grid-cols-[1fr_auto_1fr] items-center gap-3">
        <h3 className="col-start-2 text-center text-base font-bold">{title}</h3>
        <div className="justify-self-end">{exportMenu}</div>
      </div>
      {canvas}
    </div>
  );
};
